import React, { useState, useEffect, useReducer, useContext } from "react";
import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import IconButton from "@material-ui/core/IconButton";
import SearchIcon from "@material-ui/icons/Search";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import CircularProgress from "@material-ui/core/CircularProgress";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import Chip from "@material-ui/core/Chip";
import Typography from "@material-ui/core/Typography";
import PersonOutlineIcon from "@material-ui/icons/PersonOutline";
import AddIcon from "@material-ui/icons/Add";
import { Avatar, Grid } from "@material-ui/core";

import MainHeader from "../../components/MainHeader";
import whatsappIcon from "../../assets/nopicture.png";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import UserModal from "../../components/UserModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import UserStatusIcon from "../../components/UserModal/statusIcon";
import { getBackendUrl } from "../../config";
import { AuthContext } from "../../context/Auth/AuthContext";
import ForbiddenPage from "../../components/ForbiddenPage";
import { KoraxPage } from "../../components/KoraxUI";

const backendUrl = getBackendUrl();

const reducer = (state, action) => {
  if (action.type === "LOAD_USERS") {
    const users = action.payload;
    const newUsers = [];

    users.forEach((user) => {
      const userIndex = state.findIndex((u) => u.id === user.id);
      if (userIndex !== -1) {
        state[userIndex] = user;
      } else {
        newUsers.push(user);
      }
    });

    return [...state, ...newUsers];
  }

  if (action.type === "UPDATE_USERS") {
    const user = action.payload;
    const userIndex = state.findIndex((u) => u.id === user.id);

    if (userIndex !== -1) {
      state[userIndex] = user;
      return [...state];
    }
    return [user, ...state];
  }

  if (action.type === "DELETE_USER") {
    const userId = action.payload;

    const userIndex = state.findIndex((u) => u.id === userId);
    if (userIndex !== -1) {
      state.splice(userIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles((theme) => ({
  pageRoot: {
    flex: "1 1 auto",
    width: "100%",
    maxWidth: "100%",
    minHeight: 0,
    minWidth: 0,
    padding: theme.spacing(2),
    height: "100%",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",

    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1),
      overflowY: "auto",
      overflowX: "hidden",
      WebkitOverflowScrolling: "touch"
    }
  },
  hero: {
    flexShrink: 0, borderRadius: 16, padding: theme.spacing(1.5, 2.5),
    marginBottom: theme.spacing(1.5),
    background: theme.palette.type === "dark"
      ? "linear-gradient(135deg, #0A1628 0%, #0D1F3C 100%)"
      : "linear-gradient(135deg, #EBF3FF 0%, #D6E9FF 100%)",
    border: theme.palette.type === "dark"
      ? "1px solid rgba(0,140,255,0.18)" : "1px solid rgba(0,102,255,0.12)",
    boxShadow: "0 4px 16px rgba(0,102,255,0.07)",
    display: "flex", alignItems: "center", justifyContent: "space-between",
    gap: theme.spacing(2), position: "relative", overflow: "hidden",
  },
  heroBgCircle: {
    position: "absolute", right: -20, top: -20, width: 140, height: 140,
    borderRadius: "50%", pointerEvents: "none",
    background: "radial-gradient(circle, rgba(0,102,255,0.07) 0%, transparent 70%)",
  },
  heroLeft: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    zIndex: 1,
    minWidth: 0
  },
  heroIcon: {
    width: 48, height: 48, borderRadius: 14, flexShrink: 0, fontSize: "1.3rem",
    background: "linear-gradient(135deg, #0066FF 0%, #00A3FF 100%)",
    display: "flex", alignItems: "center", justifyContent: "center",
    boxShadow: "0 6px 16px rgba(0,102,255,0.3)",
  },
  heroTitle: {
    fontWeight: 800, fontSize: "1.1rem", letterSpacing: "-0.3px",
    color: theme.palette.type === "dark" ? "#ffffff" : "#0A1628",
  },
  heroSubtitle: {
    fontSize: "0.75rem", marginTop: 2,
    color: theme.palette.type === "dark" ? "rgba(255,255,255,0.5)" : "rgba(10,22,40,0.55)",
  },
  heroRight: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    zIndex: 1,
    flexShrink: 0
  },
  heroChip: {
    background: theme.palette.type === "dark" ? "rgba(255,255,255,0.07)" : "rgba(255,255,255,0.9)",
    border: "1px solid rgba(0,102,255,0.15)", borderRadius: 10,
    fontWeight: 700, fontSize: "0.72rem",
    color: theme.palette.type === "dark" ? "rgba(255,255,255,0.7)" : "#0A1628",
  },
  headerChip: {
    backgroundColor: "#EAF1FF", color: "#2f4b7c",
    border: "1px solid #d7e5ff", fontWeight: 500, fontSize: "0.72rem", height: 24,
  },
  controlsPaper: {
    marginBottom: theme.spacing(2),
    borderRadius: 14,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "0 8px 22px rgba(15, 23, 42, 0.08)",
    padding: theme.spacing(1.25),
    backgroundColor: theme.palette.background.paper,
  },
  searchField: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 10,
      backgroundColor: theme.palette.background.default,
    },
    "& .MuiInputBase-input": {
      fontSize: "0.82rem",
      paddingTop: 13,
      paddingBottom: 13,
    },
  },
  actionButton: {
    minHeight: 42,
    borderRadius: 10,
    fontWeight: 600,
    fontSize: "0.75rem",
    padding: theme.spacing(0.8, 1.4),
    boxShadow: "0 6px 14px rgba(7, 64, 171, 0.14)",
  },
  mainPaper: {
    flex: 1,
    overflowY: "auto",
    ...theme.scrollbarStyles,
    borderRadius: 14,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "0 12px 26px rgba(17, 24, 39, 0.09)",
  },
  tableHeaderCell: {
    fontWeight: 700,
    color: theme.palette.text.secondary,
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,0.04)"
        : "rgba(15, 23, 42, 0.03)",
    fontSize: "0.76rem",
  },
  tableRow: {
    transition: "background-color 0.2s ease",
    "&:hover": {
      backgroundColor:
        theme.palette.type === "dark"
          ? "rgba(255,255,255,0.03)"
          : "rgba(15, 23, 42, 0.035)",
    },
  },
  tableCellText: {
    fontSize: "0.78rem",
  },
  userAvatar: {
    width: theme.spacing(5),
    height: theme.spacing(5),
  },
  avatarDiv: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  actionCell: {
    minWidth: 120,
  },
  actionIconButton: {
    border: `1px solid ${theme.palette.divider}`,
    margin: theme.spacing(0, 0.2),
  },
  desktopOnly: {
    display: "block",
    width: "100%",
    minWidth: 0,

    [theme.breakpoints.down("sm")]: {
      display: "none"
    }
  },

  mobileOnly: {
    display: "none",

    [theme.breakpoints.down("sm")]: {
      display: "block",
      width: "100%",
      padding: theme.spacing(1)
    }
  },

  mobileCardsList: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1)
  },

  userCard: {
    width: "100%",
    boxSizing: "border-box",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 16,
    padding: theme.spacing(1.5),
    background:
      theme.palette.type === "dark"
        ? "rgba(15,23,42,.92)"
        : "#FFFFFF",
    boxShadow:
      theme.palette.type === "dark"
        ? "0 10px 26px rgba(0,0,0,.24)"
        : "0 8px 22px rgba(15,23,42,.055)"
  },

  userCardHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.2),
    minWidth: 0
  },

  mobileAvatar: {
    width: 46,
    height: 46,
    flexShrink: 0,
    border: "2px solid rgba(0,102,255,.10)",
    boxShadow: "0 5px 14px rgba(15,23,42,.08)"
  },

  userCardIdentity: {
    flex: 1,
    minWidth: 0
  },

  userCardName: {
    color: theme.palette.text.primary,
    fontSize: ".92rem",
    fontWeight: 800,
    lineHeight: 1.25,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis"
  },

  userCardEmail: {
    marginTop: 3,
    color: theme.palette.text.secondary,
    fontSize: ".72rem",
    lineHeight: 1.3,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis"
  },

  userCardStatus: {
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 30,
    height: 30,
    borderRadius: 10,
    background:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,.05)"
        : "#F8FAFC",
    border: `1px solid ${theme.palette.divider}`
  },

  userCardMeta: {
    marginTop: theme.spacing(1.25),
    display: "grid",
    gridTemplateColumns: "repeat(3,minmax(0,1fr))",
    gap: theme.spacing(.7)
  },

  userMetaBox: {
    minWidth: 0,
    padding: "9px 8px",
    borderRadius: 11,
    background:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,.035)"
        : "#F8FAFC",
    border: `1px solid ${theme.palette.divider}`
  },

  userMetaLabel: {
    color: theme.palette.text.secondary,
    fontSize: ".57rem",
    fontWeight: 800,
    lineHeight: 1.2,
    letterSpacing: ".04em",
    textTransform: "uppercase"
  },

  userMetaValue: {
    marginTop: 4,
    color: theme.palette.text.primary,
    fontSize: ".74rem",
    fontWeight: 750,
    lineHeight: 1.2,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis"
  },

  userCardFooter: {
    marginTop: theme.spacing(1.2),
    paddingTop: theme.spacing(1.1),
    borderTop: `1px solid ${theme.palette.divider}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1)
  },

  userIdBadge: {
    color: theme.palette.text.secondary,
    fontSize: ".65rem",
    fontWeight: 700
  },

  mobileActions: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(.7)
  },

  mobileActionButton: {
    width: 34,
    height: 34,
    padding: 0,
    borderRadius: 10,
    border: `1px solid ${theme.palette.divider}`,
    background:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,.04)"
        : "#FFFFFF",

    "& svg": {
      fontSize: 18
    }
  },

  editAction: {
    color: "#0066FF"
  },

  deleteAction: {
    color: "#EF4444"
  },

  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(3),
  },
  loadingText: {
    marginLeft: theme.spacing(2),
    fontSize: "0.8rem",
  },
}));

const Users = () => {
  const classes = useStyles();

  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [deletingUser, setDeletingUser] = useState(null);
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [users, dispatch] = useReducer(reducer, []);
  const { user: loggedInUser, socket } = useContext(AuthContext);
  const { profileImage } = loggedInUser;

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const fetchUsers = async () => {
      try {
        const { data } = await api.get("/users/", {
          params: { searchParam, pageNumber },
        });
        dispatch({ type: "LOAD_USERS", payload: data.users });
        setHasMore(data.hasMore);
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    };
    fetchUsers();
  }, [searchParam, pageNumber]);

  useEffect(() => {
    if (loggedInUser) {
      const companyId = loggedInUser.companyId;
      const onCompanyUser = (data) => {
        if (data.action === "update" || data.action === "create") {
          dispatch({ type: "UPDATE_USERS", payload: data.user });
        }
        if (data.action === "delete") {
          dispatch({ type: "DELETE_USER", payload: +data.userId });
        }
      };
      socket.on(`company-${companyId}-user`, onCompanyUser);
      return () => {
        socket.off(`company-${companyId}-user`, onCompanyUser);
      };
    }
  }, [socket, loggedInUser]);

  const handleOpenUserModal = () => {
    setSelectedUser(null);
    setUserModalOpen(true);
  };

  const handleCloseUserModal = () => {
    setSelectedUser(null);
    setUserModalOpen(false);
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setUserModalOpen(true);
  };

  const handleDeleteUser = async (userId) => {
    try {
      await api.delete(`/users/${userId}`);
      toast.success(i18n.t("users.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingUser(null);
    setSearchParam("");
    setPageNumber(1);
  };

  const loadMore = () => {
    setLoadingMore(true);
    setPageNumber((prevPage) => prevPage + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const renderProfileImage = (user) => {
    const src =
      user.id === loggedInUser.id
        ? profileImage
          ? `${backendUrl}/public/avatar/${profileImage}`
          : whatsappIcon
        : user.profileImage
          ? `${backendUrl}/public/avatar/${user.profileImage}`
          : whatsappIcon;

    return <Avatar src={src} alt={user.name} className={classes.userAvatar} />;
  };

  return (
    <KoraxPage className={classes.pageRoot}>
      <ConfirmationModal
        title={
          deletingUser &&
          `${i18n.t("users.confirmationModal.deleteTitle")} ${deletingUser.name}?`
        }
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={() => handleDeleteUser(deletingUser.id)}
      >
        {i18n.t("users.confirmationModal.deleteMessage")}
      </ConfirmationModal>

      <UserModal
        open={userModalOpen}
        onClose={handleCloseUserModal}
        aria-labelledby="form-dialog-title"
        userId={selectedUser && selectedUser.id}
        key={i18n.language}
      />

      {loggedInUser.profile === "user" ? (
        <ForbiddenPage />
      ) : (
        <>
          <MainHeader>
            <Grid container style={{ width: "100%" }}>
              <Grid item xs={12}>
                <div className={classes.hero}>
                  <div className={classes.heroBgCircle} />
                  <div className={classes.heroLeft}>
                    <div className={classes.heroIcon}>
                      <PersonOutlineIcon style={{ fontSize: 24, color: "#FFFFFF" }} />
                    </div>
                    <div>
                      <Typography className={classes.heroTitle}>{i18n.t("users.title")}</Typography>
                      <Typography className={classes.heroSubtitle}>
                        Gerencie acessos, perfis e jornada dos usuarios da operacao
                      </Typography>
                    </div>
                  </div>
                  <div className={classes.heroRight}>
                    <Chip
                      icon={<PersonOutlineIcon style={{ fontSize: 14 }} />}
                      label={users.length + " usuarios"}
                      className={classes.heroChip}
                      size="small"
                    />
                  </div>
                </div>
              </Grid>

              <Grid item xs={12}>
                <Paper elevation={0} className={classes.controlsPaper}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={8} md={9}>
                      <TextField
                        fullWidth
                        placeholder={i18n.t("contacts.searchPlaceholder")}
                        type="search"
                        value={searchParam}
                        onChange={handleSearch}
                        variant="outlined"
                        className={classes.searchField}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon style={{ color: "gray" }} />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4} md={3}>
                      <Button
                        fullWidth
                        variant="contained"
                        color="primary"
                        onClick={handleOpenUserModal}
                        className={classes.actionButton}
                        startIcon={<AddIcon />}
                      >
                        {i18n.t("users.buttons.add")}
                      </Button>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            </Grid>
          </MainHeader>

          <Paper
            className={classes.mainPaper}
            variant="outlined"
            onScroll={handleScroll}
          >
            <div className={classes.desktopOnly}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell align="center" className={classes.tableHeaderCell}>{i18n.t("users.table.ID")}</TableCell>
                    <TableCell align="center" className={classes.tableHeaderCell}>{i18n.t("users.table.status")}</TableCell>
                    <TableCell align="center" className={classes.tableHeaderCell}>Avatar</TableCell>
                    <TableCell align="center" className={classes.tableHeaderCell}>{i18n.t("users.table.name")}</TableCell>
                    <TableCell align="center" className={classes.tableHeaderCell}>{i18n.t("users.table.email")}</TableCell>
                    <TableCell align="center" className={classes.tableHeaderCell}>{i18n.t("users.table.profile")}</TableCell>
                    <TableCell align="center" className={classes.tableHeaderCell}>{i18n.t("users.table.startWork")}</TableCell>
                    <TableCell align="center" className={classes.tableHeaderCell}>{i18n.t("users.table.endWork")}</TableCell>
                    <TableCell align="center" className={classes.tableHeaderCell}>{i18n.t("users.table.actions")}</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {users.map(user => (
                    <TableRow key={user.id} className={classes.tableRow} hover>
                      <TableCell align="center" className={classes.tableCellText}>{user.id}</TableCell>
                      <TableCell align="center" className={classes.tableCellText}><UserStatusIcon user={user} /></TableCell>
                      <TableCell align="center">
                        <div className={classes.avatarDiv}>{renderProfileImage(user)}</div>
                      </TableCell>
                      <TableCell align="center" className={classes.tableCellText}>{user.name}</TableCell>
                      <TableCell align="center" className={classes.tableCellText}>{user.email}</TableCell>
                      <TableCell align="center" className={classes.tableCellText}>
                        <Chip
                          size="small"
                          label={user.profile}
                          style={{
                            height: 22,
                            fontSize: ".66rem",
                            fontWeight: 700,
                            textTransform: "capitalize"
                          }}
                        />
                      </TableCell>
                      <TableCell align="center" className={classes.tableCellText}>{user.startWork}</TableCell>
                      <TableCell align="center" className={classes.tableCellText}>{user.endWork}</TableCell>
                      <TableCell align="center" className={classes.actionCell}>
                        <IconButton
                          size="small"
                          className={classes.actionIconButton}
                          onClick={() => handleEditUser(user)}
                        >
                          <EditIcon />
                        </IconButton>

                        <IconButton
                          size="small"
                          className={classes.actionIconButton}
                          onClick={() => {
                            setConfirmModalOpen(true);
                            setDeletingUser(user);
                          }}
                        >
                          <DeleteOutlineIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}

                  {loadingMore && (
                    <TableRow>
                      <TableCell colSpan={9} align="center">
                        <CircularProgress size={28} />
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>

            <div className={classes.mobileOnly}>
              <div className={classes.mobileCardsList}>
                {users.map(user => (
                  <div key={`mobile-user-${user.id}`} className={classes.userCard}>
                    <div className={classes.userCardHeader}>
                      {React.cloneElement(renderProfileImage(user), {
                        className: classes.mobileAvatar
                      })}

                      <div className={classes.userCardIdentity}>
                        <Typography className={classes.userCardName}>
                          {user.name || "Usuário sem nome"}
                        </Typography>

                        <Typography className={classes.userCardEmail}>
                          {user.email || "E-mail não informado"}
                        </Typography>
                      </div>

                      <div className={classes.userCardStatus}>
                        <UserStatusIcon user={user} />
                      </div>
                    </div>

                    <div className={classes.userCardMeta}>
                      <div className={classes.userMetaBox}>
                        <div className={classes.userMetaLabel}>Perfil</div>
                        <div className={classes.userMetaValue}>
                          {user.profile || "-"}
                        </div>
                      </div>

                      <div className={classes.userMetaBox}>
                        <div className={classes.userMetaLabel}>Início</div>
                        <div className={classes.userMetaValue}>
                          {user.startWork || "--:--"}
                        </div>
                      </div>

                      <div className={classes.userMetaBox}>
                        <div className={classes.userMetaLabel}>Fim</div>
                        <div className={classes.userMetaValue}>
                          {user.endWork || "--:--"}
                        </div>
                      </div>
                    </div>

                    <div className={classes.userCardFooter}>
                      <span className={classes.userIdBadge}>
                        ID #{user.id}
                      </span>

                      <div className={classes.mobileActions}>
                        <IconButton
                          className={`${classes.mobileActionButton} ${classes.editAction}`}
                          onClick={() => handleEditUser(user)}
                          aria-label={`Editar ${user.name}`}
                        >
                          <EditIcon />
                        </IconButton>

                        <IconButton
                          className={`${classes.mobileActionButton} ${classes.deleteAction}`}
                          onClick={() => {
                            setConfirmModalOpen(true);
                            setDeletingUser(user);
                          }}
                          aria-label={`Excluir ${user.name}`}
                        >
                          <DeleteOutlineIcon />
                        </IconButton>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {loading && !loadingMore && (
              <div className={classes.loadingContainer}>
                <CircularProgress size={30} />
                <span className={classes.loadingText}>
                  {i18n.t("loading")}
                </span>
              </div>
            )}
          </Paper>
        </>
      )}
    </KoraxPage>
  );
};

export default Users;
